import { useContext, useEffect } from "react";
import { Router } from "@reach/router";
import { connect, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import { ContextMenu } from "../components/ContextMenu";
import {
	getDocuments,
	getPastProjects,
	getResources,
} from "actions/companyActions";

//Routes
import { MyAccount } from "../routes/account/MyAccount";
import { Breadcrumbs } from "components/Breadcrumb";
import Projects from "pages/RFQ/Projects/Projects";

import SupplierLists from "pages/RFQ/SupplierList/SupplierLists";
import { ProjectForm } from "pages/RFQ/Projects/ProjectForm";
import ProjectDetail from "pages/RFQ/Projects/ProjectDetail/ProjectDetail";
import SupplierForm from "pages/RFQ/SupplierList/SupplierForm";
import { SupplierDetail } from "pages/RFQ/SupplierList/SupplierDetail";
import { RFQ } from "pages/RFQ/RFQ";
import CreateRFQPage from "newui/features/Rfx/Creation/CreateRFQPage";
import { RFQDetail } from "pages/RFQ/RFQ/RFQDetail";
import { NotFound } from "./NotFound";
import { Search } from "routes/search/Search";
import AccountSettings from "./accountsettings/AccountSettings";
import { AddResource } from "components/ResourceForm/AddResource";
import { EditResource } from "components/ResourceForm/EditResource";
import { AddProject } from "components/ProjectForm/AddProject";
import { EditProject } from "components/ProjectForm/EditProject";
import { DocumentRequest } from "components/DocumentRequest";
import { EditUserProfile } from "components/EditUserProfile";
import { UpdateCreditCard } from "components/CreditCardDetail";
import { CreateUserForm } from "components/UserProfile";
import ViewRFQPage from "pages/RFQ/RFQ/ViewRFQ/ViewRFQPage";
import { Invoice } from "components/Invoice";
import { ProfileContext } from "context/ProfileContext";
import { CompanyListing } from "./CompanyListing";
import { EditUserAccount } from "components/EditUserAccount";
import { ManageTemplates } from "pages/RFQ/Templates/ManageTemplates";
import { ProtectedTemplateRoute } from "pages/RFQ/Templates/ProtectedTemplateRoute";
import { ProcurerDashboard } from "features/dashboard-procurer";
import { Box } from "@chakra-ui/react";
import { ContentContainer } from "components/Layout";
import { useCurrentUserHasDashboard } from "hooks/featureFlags";
import RFQPreview from "newui/features/Rfx/Submission/RFQPreview";
import { RfxDataProvider } from "newui/features/Rfx/data-provider/RfxDataProvider";
import { RfxSubmissionDataProvider } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import CreateRfxTemplate from "newui/features/Rfx/Creation/CreateRFxTemplate";
import GetRfxSubmissionByCompany from "app/GetRfxSubmissionByCompany";
import { useCurrentCompany, useCurrentUser } from "hooks/auth";
import { CompanyProfile } from "newui/features/Company";
import { selectAuthLoading } from "newui/features/Auth/authSelectors";

export const AccountComponent = ({
	getPastProjects,
	getResources,
	getDocuments,
	path, // eslint-disable-line no-unused-vars
}) => {
	const profileContext = useContext(ProfileContext);
	const isLoading = useSelector(selectAuthLoading);
	const currentCompany = useCurrentCompany();
	const { currentUser } = useCurrentUser();
	const companyId = currentCompany?.id;
	const hasDashboardAccess = useCurrentUserHasDashboard();

	useEffect(() => {
		//Set this company ID from the route variable - this allows anyone to access/edit all companies

		if (companyId) {
			getPastProjects(companyId);
			getResources(companyId);
			getDocuments(companyId);
		}
	}, [
		currentUser,
		currentCompany,
		getPastProjects,
		getDocuments,
		getResources,
		companyId,
	]);

	if (isLoading) {
		return (
			<div className="flex-grow p-[50px]">
				<Skeleton
					duration={0.5}
					count={10}
					width="100%"
				/>
			</div>
		);
	}

	return (
		<Box
			display="flex"
			flexWrap="nowrap"
			width="100%"
			minH="100vh"
		>
			<ContextMenu />
			<ContentContainer
				id="element-scroll"
				noPadding={
					window?.location?.href.endsWith("/search") ||
					window?.location?.href.includes("/search/")
				}
			>
				<Breadcrumbs
					ignoreCompany={false}
					templateRoutes={{
						new: "Create RFx Template",
						edit: "Edit RFx Template",
						"manage-templates": "Manage Templates",
					}}
					parentPath={{
						"rfx-template": "manage-templates",
					}}
				/>

				<Router primary={false}>
					<NotFound default />
					<MyAccount path="my-account" />

					{/* START OF ACCOUNT SETTING MENU */}
					<AccountSettings
						path="account-settings"
						company={currentCompany}
					/>
					<EditUserAccount path="edit-user-account" />
					<Invoice
						path="account-settings/invoice"
						company={currentCompany}
					/>
					<CreateUserForm path="account-settings/create-user" />
					<UpdateCreditCard path="account-settings/update-card-details" />
					{/* END OF ACCOUNT SETTING MENU */}

					{/* START OF COMPANIES LISTING */}
					<CompanyListing path="companies" />
					<CompanyListing
						path="accounts/*"
						admin
					/>
					{/* END OF COMPANIES LISTING */}

					{/* START OF COMPANY PROFILE MENU */}
					<CompanyProfile
						path="company-profile/:activeTab"
						company={currentCompany}
					/>

					<EditUserProfile
						path="company-profile/edit-user-profile"
						company={"My Profile"}
					/>

					<AddResource path="company-profile/:activeTab/new-resource" />
					{currentCompany && (
						<EditResource path="company-profile/:activeTab/edit-resource/:resourceId" />
					)}
					<AddProject
						path="company-profile/:activeTab/create-project"
						profileTypeId={currentCompany?.profile_type?.id}
					/>
					<EditProject
						path="company-profile/:activeTab/edit-project/:projectId"
						profileTypeId={currentCompany?.profile_type?.id}
					/>

					{/* END OF COMPANY PROFILE MENU */}

					{/* RFx (RFx and RFQ are same) */}
					{profileContext?.isRfqFeatureEnabled && (
						<>
							{hasDashboardAccess ? (
								<ProcurerDashboard path="/" />
							) : (
								<Projects path="/" />
							)}
							{hasDashboardAccess && <ProcurerDashboard path="dashboard/*" />}

							<Projects path="projects" />
							<ProjectForm
								path="projects/new-project"
								newProject
							/>
							<ProjectForm
								path="projects/new-project/suppliers-list/:supplierListId"
								newProjectFromSupplierList
							/>
							<ProjectForm
								path="projects/edit-project/:projectId"
								editProject
							/>
							<ProjectDetail path="projects/:id/:slug" />

							<SupplierDetail path="supplier-list/:id/:slug" />
							<SupplierLists path="supplier-list" />
							<SupplierForm
								path="supplier-list/new-supplier-list"
								isNew
							/>
							<SupplierForm
								path="supplier-list/edit-supplier-list/:listId"
								isEdit
							/>

							<RFQ path="rfx" />

							<GetRfxSubmissionByCompany
								companyId={companyId}
								path="rfx/:rfxId/getsubmission/:submissionCompanyId/:currentTab"
							/>
							<RfxSubmissionDataProvider
								path="rfx-template"
								useParentRfxData={false}
							>
								<CreateRfxTemplate
									path="new"
									templateId={({ templateId }) => templateId}
								/>
								<ProtectedTemplateRoute
									path=":templateId"
									isEdit
									isTemplate
									templateId={({ templateId }) => templateId}
								/>
							</RfxSubmissionDataProvider>
							<RfxDataProvider path="rfx/:rfxRouteId">
								{/* Client viewing sent RFx's */}
								<RFQDetail path=":slug/:currentTab/:contextId" />
								<RFQDetail path=":slug/:currentTab" />
								<RFQDetail path="/" />

								{/* Client Creating RFx and RFx templates */}
								<RfxSubmissionDataProvider path="/">
									<CreateRFQPage path="create-rfx" />
									<CreateRFQPage
										path="edit-rfx"
										isEdit
									/>
									<ViewRFQPage path="view" />
								</RfxSubmissionDataProvider>

								{/* Client Viewing submissions */}
								<RfxSubmissionDataProvider path="submission/:rfxInviteRouteId/">
									<RFQPreview path=":currentTab" />
								</RfxSubmissionDataProvider>
							</RfxDataProvider>
							<ManageTemplates path="manage-templates" />
						</>
					)}
					{/* END OF RFx */}

					<Search
						path="search/*"
						title="Search"
					/>
					<DocumentRequest path="company-profile/:activeTab/request-view" />
				</Router>
			</ContentContainer>
		</Box>
	);
};

export const Account = connect(null, {
	getDocuments,
	getPastProjects,
	getResources,
})(AccountComponent);
